import scorableItemReader from '../store/readHelpers/ScorableItemReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import utilService from '../services/UtilService';
import { reportError } from '../services/ErrorReporting';
import scorableItemNumberingService from '../services/questionView/ScorableItemNumberingService';
import isDrawingResponseIdentifier from '../services/DrawingUtils';

export function getUserResponseForResponseIdentifier(identifier, itemResult) {
    const responseIdentifier = itemResult.responseIdentifiers.find((item) => {
        return item.identifier === identifier;
    });

    return responseIdentifier?.userResponse ? responseIdentifier.userResponse : [];
}

export function getScorableItemsStatus(contentRevisionId, itemResult, scorableItems) {
    if (scorableItems === undefined) {
        return undefined;
    }
    return scorableItems
        .map((scorableItem) => {
            return {
                ...scorableItem,
                isAttempted: scorableItemNumberingService.isAttemptedScorableItem(
                    contentRevisionId,
                    scorableItem,
                    itemResult
                ),
            };
        });
}

export function getResponseStatus(itemResult, question, scorableItems) {
    let atLeastOneInteractionRepliedTo = false;
    let allDeclarationsRepliedTo;
    let scorableItemsStatus;

    if (question?.responseDeclarations) {
        question.responseDeclarations.forEach((declaration) => {
            const { identifier } = declaration;

            if (identifier === 'HINTREQUEST') {
                return;
            }

            if (isDrawingResponseIdentifier(identifier)) {
                return;
            }

            const userResponse = getUserResponseForResponseIdentifier(identifier, itemResult) || [];
            let repliedTo = userResponse.length > 0;

            if (repliedTo && utilService.arraysHaveIdenticalValues(userResponse, declaration.defaultValue)) {
                repliedTo = false;
            }

            if (repliedTo) {
                atLeastOneInteractionRepliedTo = true;
                if (allDeclarationsRepliedTo === undefined) {
                    allDeclarationsRepliedTo = true;
                }
            } else {
                allDeclarationsRepliedTo = false;
            }
        });

        scorableItemsStatus = getScorableItemsStatus(question.contentRevisionId, itemResult, scorableItems);
    }

    return {
        atLeastOneInteractionRepliedTo,
        allDeclarationsRepliedTo,
        scorableItemsStatus,
    };
}


const responseActions = {
    types: {
        RESPONSE_ADD_SECONDS_TO_DURATION: 'RESPONSE_ADD_SECONDS_TO_DURATION',
        RESPONSE_REMOVE_ALL_ITEM_RESULTS: 'RESPONSE_REMOVE_ALL_ITEM_RESULTS',
        RESPONSE_REMOVE_RESPONSES: 'RESPONSE_REMOVE_RESPONSES',
        RESPONSE_RESET_ITEM_RESULT: 'RESPONSE_RESET_ITEM_RESULT',
        RESPONSE_RESET_QUESTION_COMMIT_TIMESTAMP: 'RESPONSE_RESET_QUESTION_COMMIT_TIMESTAMP',
        RESPONSE_SET_CONFIRM_QUESTION_COMMIT_TIMESTAMP: 'RESPONSE_SET_CONFIRM_QUESTION_COMMIT_TIMESTAMP',
        RESPONSE_SET_INTERACTION_RESPONSE: 'RESPONSE_SET_INTERACTION_RESPONSE',
        RESPONSE_SET_ITEM_RESULT: 'RESPONSE_SET_ITEM_RESULT',
        RESPONSE_SET_OUTCOME_VARIABLES: 'RESPONSE_SET_OUTCOME_VARIABLES',
        RESPONSE_SET_OWN_OFFLINE_COMMIT_ATTEMPT: 'RESPONSE_SET_OWN_OFFLINE_COMMIT_ATTEMPT',
        RESPONSE_SET_PENDING_QUESTION_COMMIT_TIMESTAMP: 'RESPONSE_SET_PENDING_QUESTION_COMMIT_TIMESTAMP',
        RESPONSE_SET_QUESTION_ANNOTATIONS: 'RESPONSE_SET_QUESTION_ANNOTATIONS',
        RESPONSE_SET_QUESTION_COMMIT_TIMESTAMP: 'RESPONSE_SET_QUESTION_COMMIT_TIMESTAMP',
        RESPONSE_SET_STATE: 'RESPONSE_SET_STATE',
        RESPONSE_SET_STUDENT_HIGHLIGHTS: 'RESPONSE_SET_STUDENT_HIGHLIGHTS',
    },

    commitTimestampStatus: {
        CONFIRM: 'CONFIRM',
        PENDING: 'PENDING',
        STORED: 'STORED',
    },

    setInteractionResponse(question, contentRevisionId, identifier, response, extraResponses) {
        return {
            type: this.types.RESPONSE_SET_INTERACTION_RESPONSE,
            question,
            contentRevisionId,
            identifier,
            response,
            extraResponses,
            scorableItems: scorableItemReader.getScorableItemsInQuestion(contentRevisionId),
        };
    },

    setItemResult(question, contentRevisionId, itemResult) {
        return {
            type: this.types.RESPONSE_SET_ITEM_RESULT,
            contentRevisionId,
            itemResult,
            question,
            scorableItems: scorableItemReader.getScorableItemsInQuestion(contentRevisionId),
        };
    },

    setOutcomeVariables(contentRevisionId, feedback, score) {
        const outcomeVariables = { FEEDBACK: {
            baseType: 'identifier',
            cardinality: 'single',
            identifier: 'FEEDBACK',
            value: feedback,
        } };
        if (score !== undefined) {
            outcomeVariables.SCORE = {
                baseType: 'float',
                cardinality: 'single',
                identifier: 'SCORE',
                value: score,
            };
        }
        return { type: this.types.RESPONSE_SET_OUTCOME_VARIABLES, outcomeVariables, contentRevisionId };
    },

    resetItemResult(contentRevisionId) {
        return { type: this.types.RESPONSE_RESET_ITEM_RESULT, contentRevisionId };
    },

    setResponseState(itemResults) {
        const allScorableItems = scorableItemReader.getScorableItems();
        const newState = { ...itemResults, status: {} };

        Object
            .keys(itemResults)
            .forEach((key) => {
                const contentRevisionId = parseInt(key, 10);
                const itemResult = itemResults[contentRevisionId];

                const question = assessmentReader.getQuestion(contentRevisionId);
                if (!question) {
                    reportError(`ItemResult/QuestionSet inconsistency! ContentRevisionId ${contentRevisionId} not found in question set`);
                }

                newState.status[contentRevisionId] = getResponseStatus(
                    itemResult,
                    question,
                    scorableItemReader.getScorableItemsInQuestion(contentRevisionId, allScorableItems),
                );
            });

        return { type: this.types.RESPONSE_SET_STATE, newState };
    },

    addSecondsToDuration(contentRevisionId, seconds) {
        return { type: this.types.RESPONSE_ADD_SECONDS_TO_DURATION, contentRevisionId, seconds };
    },

    setStudentHighlights(contentRevisionId, highlights) {
        return { type: this.types.RESPONSE_SET_STUDENT_HIGHLIGHTS, contentRevisionId, highlights };
    },

    setQuestionAnnotations(contentRevisionId, annotations) {
        return { type: this.types.RESPONSE_SET_QUESTION_ANNOTATIONS, contentRevisionId, annotations };
    },

    removeAllItemResultsInState() {
        return { type: this.types.RESPONSE_REMOVE_ALL_ITEM_RESULTS };
    },

    removeResponses(questionIds) {
        return { type: this.types.RESPONSE_REMOVE_RESPONSES, questionIds };
    },

    setConfirmQuestionCommitTimestamp(questionId) {
        return { type: this.types.RESPONSE_SET_CONFIRM_QUESTION_COMMIT_TIMESTAMP, questionId };
    },

    setPendingQuestionCommitTimestamp(questionId) {
        return { type: this.types.RESPONSE_SET_PENDING_QUESTION_COMMIT_TIMESTAMP, questionId };
    },

    setStoredQuestionCommitTimestamp(questionId) {
        return { type: this.types.RESPONSE_SET_QUESTION_COMMIT_TIMESTAMP, questionId };
    },

    resetQuestionCommitTimestamp(questionId) {
        return { type: this.types.RESPONSE_RESET_QUESTION_COMMIT_TIMESTAMP, questionId };
    },

    // sets true (depending on player settings) when a TT tries to commit question timestamp being offline
    setOneWayNavigationOfflineCommitAttempt(isOfflineCommitAttempting) {
        return { type: this.types.RESPONSE_SET_OWN_OFFLINE_COMMIT_ATTEMPT, isOfflineCommitAttempting };
    },
};

export default responseActions;
